import React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  Box,
} from '@mui/material';
import { Formik, Form } from 'formik';
import useSessionTimeRangeService from '../../api/services/SessionTimeRangeService';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

const SessionTimeRangeSchema = Yup.object().shape({
  sessionTime: Yup.string()
    .required('Session time is required')
    .min(1, 'Session time must be at least 1 character'),
  timeDescription: Yup.string()
    .optional(),
});

const initialValues = {
  sessionTime: '',
  timeDescription: '',
};

const CreateDialog = ({ open, onClose, onSuccess }) => {
  const { createSessionTimeRange } = useSessionTimeRangeService();

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const sessionTimeRangeDto = {
        sessionTime: values.sessionTime,
        timeDescription: values.timeDescription || null,
      };

      await createSessionTimeRange(sessionTimeRangeDto, () => {
        resetForm();
        onSuccess?.();
        onClose();
      });
    } catch (error) {
      console.error("Create session time range failed:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || 'Failed to create session time range');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>CREATE SESSION TIME RANGE</DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={SessionTimeRangeSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => (
            <Form>
              {isSubmitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Session Time *"
                    name="sessionTime"
                    variant="filled"
                    placeholder="e.g., 08:00 - 09:00"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.sessionTime}
                    error={touched.sessionTime && Boolean(errors.sessionTime)}
                    helperText={touched.sessionTime && errors.sessionTime}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Time Description (Optional)"
                    name="timeDescription"
                    variant="filled"
                    multiline
                    rows={3}
                    placeholder="e.g., Morning Session"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.timeDescription}
                    error={touched.timeDescription && Boolean(errors.timeDescription)}
                    helperText={touched.timeDescription && errors.timeDescription}
                  />
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  Create
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;

